import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { chatWithAIStream, manageAISessions, manageAIChats } from '../lib/api';
import { MessageCircle, Plus, Send, Trash2, Edit, ChevronDown, History } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';

interface Session {
  id: string;
  title: string;
  created_at: string;
}

interface Chat {
  id: string;
  user_question: string;
  ai_answer: string;
  created_at: string;
}

const AIAssistant: React.FC = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [currentSession, setCurrentSession] = useState<Session | null>(null);
  const [chats, setChats] = useState<Chat[]>([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [showSessionDropdown, setShowSessionDropdown] = useState(false);
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchSessions();
  }, []);

  useEffect(() => {
    if (currentSession) {
      fetchChats(currentSession.id);
    }
  }, [currentSession]);

  useEffect(() => {
    scrollToBottom();
  }, [chats]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchSessions = async () => {
    try {
      const response = await manageAISessions('list', {
        page: 1,
        limit: 100 // 获取所有会话
      });

      if (!response.success) {
        throw new Error(response.error || '获取会话失败');
      }

      const sessions = response.data?.data || [];
      setSessions(sessions);
      
      // 自动选择第一个会话
      if (sessions.length > 0) {
        setCurrentSession(sessions[0]);
      }
    } catch (error) {
      console.error('获取会话失败:', error);
      toast.error('获取会话失败');
    } finally {
      setLoadingSessions(false);
    }
  };

  const fetchChats = async (sessionId: string) => {
    try {
      const response = await manageAIChats('list', {
        session_id: sessionId,
        page: 1,
        limit: 1000 // 获取所有聊天记录
      });

      if (!response.success) {
        throw new Error(response.error || '获取聊天记录失败');
      }

      const chats = response.data?.data || [];
      setChats(chats);
    } catch (error) {
      console.error('获取聊天记录失败:', error);
      toast.error('获取聊天记录失败');
    }
  };

  const createSession = async () => {
    if (!user) {
      toast.error('请先登录');
      return;
    }

    try {
      const response = await manageAISessions('create', {
        user_id: user.id,
        title: `新会话 ${new Date().toLocaleString()}`,
      });

      if (!response.success) {
        throw new Error(response.error || '创建会话失败');
      }
      
      const newSession = response.data;
      setSessions([newSession, ...sessions]);
      setCurrentSession(newSession);
      setShowSessionDropdown(false);
      toast.success('新会话创建成功！');
    } catch (error) {
      console.error('创建会话失败:', error);
      toast.error('创建会话失败');
    }
  };

  const deleteSession = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('确定要删除这个会话吗？')) return;

    try {
      // 删除会话（edge function会自动处理关联的聊天记录）
      const response = await manageAISessions('delete', { id: sessionId });

      if (!response.success) {
        throw new Error(response.error || '删除会话失败');
      }

      setSessions(sessions.filter(s => s.id !== sessionId));
      if (currentSession?.id === sessionId) {
        const remainingSessions = sessions.filter(s => s.id !== sessionId);
        setCurrentSession(remainingSessions.length > 0 ? remainingSessions[0] : null);
      }
      toast.success('会话删除成功！');
    } catch (error) {
      console.error('删除会话失败:', error);
      toast.error('删除会话失败');
    }
  };

  const selectSession = (session: Session) => {
    setCurrentSession(session);
    setShowSessionDropdown(false);
    setEditingTitle(null);
  };

  const updateSessionTitle = async () => {
    if (!editingTitle || !newTitle.trim()) return;

    try {
      const response = await manageAISessions('update', {
        id: editingTitle,
        title: newTitle.trim()
      });

      if (!response.success) {
        throw new Error(response.error || '更新会话标题失败');
      }

      // 更新本地状态
      setSessions(sessions.map(s => 
        s.id === editingTitle ? { ...s, title: newTitle.trim() } : s
      ));
      
      if (currentSession?.id === editingTitle) {
        setCurrentSession({ ...currentSession, title: newTitle.trim() });
      }

      setEditingTitle(null);
      setNewTitle('');
      toast.success('会话标题更新成功！');
    } catch (error) {
      console.error('更新会话标题失败:', error);
      toast.error('更新会话标题失败');
    }
  };

  const startEditTitle = (sessionId: string, currentTitle: string) => {
    setEditingTitle(sessionId);
    setNewTitle(currentTitle);
  };

  const cancelEditTitle = () => {
    setEditingTitle(null);
    setNewTitle('');
  };

  const sendMessage = async () => {
    if (!message.trim() || !currentSession || loading || isStreaming || !user) return;

    const userMessage = message.trim();
    setMessage('');
    setIsStreaming(true);
    setStreamingMessage('');

    try {
      // 先显示用户消息
      const tempUserChat = {
        id: 'temp-' + Date.now(),
        user_question: userMessage,
        ai_answer: '',
        created_at: new Date().toISOString()
      };
      setChats(prev => [...prev, tempUserChat]);

      // 调用流式AI聊天API
      await chatWithAIStream(
        userMessage, 
        '', 
        'general',
        undefined,
        // onChunk - 处理流式数据块
        (chunk: string) => {
          setStreamingMessage(prev => prev + chunk);
        },
        // onComplete - 流式完成后保存到数据库
        async (fullResponse: string) => {
          try {
            const response = await manageAIChats('create', {
              user_id: user.id,
              session_id: currentSession.id,
              user_question: userMessage,
              ai_answer: fullResponse,
            });

            if (!response.success) {
              throw new Error(response.error || '保存聊天记录失败');
            }

            // 重新获取聊天记录
            await fetchChats(currentSession.id);
            setStreamingMessage('');
          } catch (error) {
            console.error('保存聊天记录失败:', error);
            toast.error('保存聊天记录失败');
          }
        },
        // onError - 处理错误
        (error: string) => {
          toast.error('AI回复失败: ' + error);
          setStreamingMessage('');
          // 移除临时消息
          setChats(prev => prev.filter(chat => chat.id !== tempUserChat.id));
        }
      );

    } catch (error) {
      console.error('发送消息失败:', error);
      toast.error('发送消息失败');
      setStreamingMessage('');
    } finally {
      setIsStreaming(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loadingSessions) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">AI助手</h1>
          <p className="text-lg text-gray-600">
            与AI助手对话，获得学习指导和问题解答
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm h-[calc(100vh-12rem)] sm:h-[700px] flex flex-col">
          {/* Session Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center space-x-4">
                {/* Session Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowSessionDropdown(!showSessionDropdown)}
                    className="flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors touch-manipulation text-sm sm:text-base"
                  >
                    <History className="w-4 h-4 mr-2" />
                    {editingTitle === currentSession?.id ? (
                      <input
                        type="text"
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        onBlur={updateSessionTitle}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            updateSessionTitle();
                          } else if (e.key === 'Escape') {
                            cancelEditTitle();
                          }
                        }}
                        className="text-sm font-medium bg-white border border-gray-300 rounded px-2 py-1 max-w-32 sm:max-w-48"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <span className="text-sm font-medium truncate max-w-32 sm:max-w-none">
                        {currentSession ? currentSession.title : '选择会话'}
                      </span>
                    )}
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </button>

                  {showSessionDropdown && (
                    <div className="absolute top-full left-0 mt-1 w-72 sm:w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
                      <div className="p-2 border-b border-gray-100">
                        <button
                          onClick={createSession}
                          className="w-full flex items-center px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors touch-manipulation"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          新建会话
                        </button>
                      </div>
                      <div className="p-1">
                        {sessions.map((session) => (
                          <div
                            key={session.id}
                            className={`flex items-center justify-between p-2 rounded-lg cursor-pointer hover:bg-gray-50 touch-manipulation ${
                              currentSession?.id === session.id ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                            }`}
                            onClick={() => selectSession(session)}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{session.title}</p>
                              <p className="text-xs text-gray-500">
                                {new Date(session.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <button
                              onClick={(e) => deleteSession(session.id, e)}
                              className="ml-2 p-1 text-gray-400 hover:text-red-600 transition-colors touch-manipulation"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {currentSession && (
                  <button 
                    onClick={() => startEditTitle(currentSession.id, currentSession.title)}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors touch-manipulation"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {currentSession ? (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
                {chats.map((chat) => (
                  <div key={chat.id} className="space-y-4">
                    {/* User Message */}
                    <div className="flex justify-end">
                      <div className="max-w-[85%] sm:max-w-xs lg:max-w-md bg-blue-600 text-white rounded-lg px-3 sm:px-4 py-2 sm:py-3">
                        <p className="text-sm whitespace-pre-wrap">{chat.user_question}</p>
                      </div>
                    </div>

                    {/* AI Message with Markdown */}
                    {chat.ai_answer && (
                      <div className="flex justify-start">
                        <div className="max-w-[90%] sm:max-w-xs lg:max-w-2xl bg-gray-100 text-gray-900 rounded-lg px-3 sm:px-4 py-2 sm:py-3">
                          <div className="prose prose-sm max-w-none">
                            <ReactMarkdown
                              components={{
                                p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                                ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                                ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                                li: ({ children }) => <li className="text-sm">{children}</li>,
                                h1: ({ children }) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
                                h2: ({ children }) => <h2 className="text-base font-bold mb-2">{children}</h2>,
                                h3: ({ children }) => <h3 className="text-sm font-bold mb-1">{children}</h3>,
                                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                                em: ({ children }) => <em className="italic">{children}</em>,
                                code: ({ children }) => <code className="bg-gray-200 px-1 py-0.5 rounded text-xs font-mono break-all">{children}</code>,
                                pre: ({ children }) => <pre className="bg-gray-200 p-2 rounded text-xs overflow-x-auto mb-2 whitespace-pre-wrap">{children}</pre>,
                                blockquote: ({ children }) => <blockquote className="border-l-4 border-gray-300 pl-3 italic mb-2">{children}</blockquote>,
                              }}
                            >
                              {chat.ai_answer}
                            </ReactMarkdown>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* 流式消息显示 */}
                {isStreaming && (
                  <div className="space-y-4">
                    {/* AI流式回复 */}
                    <div className="flex justify-start">
                      <div className="max-w-[90%] sm:max-w-xs lg:max-w-2xl bg-gray-100 text-gray-900 rounded-lg px-3 sm:px-4 py-2 sm:py-3">
                        {streamingMessage ? (
                          <div className="prose prose-sm max-w-none">
                            <ReactMarkdown
                              components={{
                                p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                                ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                                ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                                li: ({ children }) => <li className="text-sm">{children}</li>,
                                h1: ({ children }) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
                                h2: ({ children }) => <h2 className="text-base font-bold mb-2">{children}</h2>,
                                h3: ({ children }) => <h3 className="text-sm font-bold mb-1">{children}</h3>,
                                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                                em: ({ children }) => <em className="italic">{children}</em>,
                                code: ({ children }) => <code className="bg-gray-200 px-1 py-0.5 rounded text-xs font-mono break-all">{children}</code>,
                                pre: ({ children }) => <pre className="bg-gray-200 p-2 rounded text-xs overflow-x-auto mb-2 whitespace-pre-wrap">{children}</pre>,
                                blockquote: ({ children }) => <blockquote className="border-l-4 border-gray-300 pl-3 italic mb-2">{children}</blockquote>,
                              }}
                            >
                              {streamingMessage}
                            </ReactMarkdown>
                          </div>
                        ) : null}
                        {/* 打字指示器 */}
                        <div className="flex items-center mt-2">
                          <div className="animate-pulse flex space-x-1">
                            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                            <div className="w-2 h-2 bg-blue-600 rounded-full" style={{ animationDelay: '0.2s' }}></div>
                            <div className="w-2 h-2 bg-blue-600 rounded-full" style={{ animationDelay: '0.4s' }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {loading && !isStreaming && (
                  <div className="flex justify-start">
                    <div className="max-w-[90%] sm:max-w-xs lg:max-w-md bg-gray-100 text-gray-900 rounded-lg px-3 sm:px-4 py-2 sm:py-3">
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-sm">AI正在思考...</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="border-t border-gray-200 p-3 sm:p-4 pb-safe">
                <div className="flex space-x-2 sm:space-x-4">
                  <div className="flex-1">
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="输入您的问题..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm sm:text-base"
                      rows={window.innerWidth < 640 ? 1 : 2}
                    />
                  </div>
                  <button
                    onClick={sendMessage}
                    disabled={!message.trim() || loading || isStreaming}
                    className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors touch-manipulation flex-shrink-0"
                  >
                    {isStreaming ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  选择或创建一个会话
                </h3>
                <p className="text-gray-600 mb-4">
                  开始与AI助手对话
                </p>
                <button
                  onClick={createSession}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors touch-manipulation"
                >
                  创建新会话
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;